package org.matthewo.routes

import io.ktor.resources.*

@Resource("/api")
data object API {

    @Resource("/games")
    data class Games(val parent: API = API)

    @Resource("/search")
    data class Search(val parent: API = API)


    @Resource("/{year}")
    data class Year(val parent: API = API, val year: Int? = null) {
        constructor(year: Int) : this(API, year)

        @Resource("/search")
        data class YearSearch(val parent: Year = Year()) {
            constructor(year: Int) : this(Year(year))
        }

        @Resource("/records/{params...}")
        data class Records(val parent: Year = Year(), val params: List<String>? = null) {
            constructor(year: Int, params: List<String>) : this(Year(year), params)

            constructor(year: Int, vararg params: Any) : this(Year(year), params.map { it.toString() })
        }

        @Resource("/region/{region}")
        data class Region(val parent: Year = Year(), val region: String? = null) {
            constructor(year: Int, region: String) : this(Year(year), region)
        }
    }


    @Resource("/team/{number}")
    data class Team(val parent: Year = Year(), val number: Int? = null) {
        constructor(year: Int, number: Int) : this(Year(year), number)

        @Resource("/contribution")
        data class Contribution(val parent: Team = Team()) {
            constructor(year: Int, number: Int) : this(Team(year, number))

            @Resource("/{code}")
            data class Event(val parent: Contribution = Contribution(), val code: String)
        }

        @Resource("/metrics/{code}")
        data class Metrics(val parent: Team = Team(), val code: String? = null) {
            constructor(year: Int, number: Int, code: String) : this(Team(year, number), code)
        }
    }


    @Resource("/event/{code}")
    data class Event(val parent: Year = Year(), val code: String? = null) {
        constructor(year: Int, code: String) : this(Year(year), code)

        @Resource("/stats")
        data class Stats(val parent: Event = Event(), val pre: Boolean = false, val advancement: Boolean = false) {
            constructor(year: Int, code: String) : this(Event(year, code))
        }

        @Resource("/records")
        data class Records(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))
        }

        @Resource("/matches")
        data class Matches(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))
        }

        @Resource("/playoffs")
        data class Playoffs(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))
        }

        @Resource("/rankings")
        data class Rankings(val parent: Event = Event(), val match: Int? = null) {
            constructor(year: Int, code: String, match: Int? = null) : this(Event(year, code), match)

            @Resource("/{team}")
            data class Team(val parent: Rankings = Rankings(), val team: Int, val match: Int? = null) {
                constructor(event: Event, team: Int, match: Int? = null) : this(Rankings(event), team, match)
                constructor(year: Int, code: String, team: Int, match: Int? = null) : this(Event(year, code), team, match)
            }
        }

        @Resource("/advancement")
        data class Advancement(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))
        }

        @Resource("/compare")
        data class Compare(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))
        }

        @Resource("/table")
        data class Table(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))

            @Resource("/teams")
            data class Teams(val parent: Table = Table()) {
                constructor(year: Int, code: String) : this(Table(year, code))
            }

            @Resource("/matches")
            data class Matches(val parent: Table = Table()) {
                constructor(year: Int, code: String) : this(Table(year, code))
            }
        }

        @Resource("/match/{id}")
        data class Match(val parent: Event = Event(), val id: String? = null) {
            constructor(year: Int, code: String, id: String) : this(Event(year, code), id)
        }

        // server-sent events with the event's data version, sent again whenever its data changes
        @Resource("/updates")
        data class Updates(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))
        }

        @Resource("/refresh")
        data class Refresh(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))
        }

        // whether a device is told about the event's results; the device's subscription is the body, which keeps its
        // endpoint, which anyone could send to, out of urls and access logs
        @Resource("/push")
        data class Push(val parent: Event = Event()) {
            constructor(year: Int, code: String) : this(Event(year, code))

            // sends a device that follows the event a notification, to show it'll get them; its subscription's the body
            @Resource("/test")
            data class Test(val parent: Push = Push()) {
                constructor(year: Int, code: String) : this(Push(year, code))
            }
        }

        @Resource("/award/{team}")
        data class SetAward(val parent: Event = Event(), val team: Int? = null) {
            constructor(year: Int, code: String, team: Int) : this(Event(year, code), team)
        }
    }


    @Resource("/admin")
    data class Admin(val parent: API = API) {
        @Resource("/users")
        data class Users(val parent: Admin = Admin())

        @Resource("/user")
        // a new user's password is sent as the request body, so it stays out of urls and access logs
        data class User(val parent: Admin = Admin(), val username: String)
    }
}


@Resource("/kill")
data object Kill
